import type { SupportedLocale } from '@erp/shared';

export const electronicInvoiceTranslations = {
  en: {
    electronicInvoices: {
      prepaid: 'Prepaid amount',
      rounding: 'Payable rounding',
      due: 'Amount due',
      advancePayment: 'Existing advance payment',
      advanceAmount: 'Amount to allocate',
      removeAdvance: 'Remove allocation',
      linkAdvance: 'Link an advance',
      reconcileAdvances: 'Reconcile advance payments',
      prepaymentNotice:
        'Select actual existing supplier payments. Allocating an advance does not create a payment. Unreconciled prepayments block approval; you can link them later in this review.',
      prepaymentPermission:
        'An accountant with banking read and posting permissions must reconcile the actual payments.',
      prepaymentUnreconciled:
        'The supplier prepaid amount is not reconciled with actual payments. Reopen the electronic invoice review and allocate the existing advances before approval.',
      prepaymentInvalid:
        'Select active payments in the same supplier and currency, with sufficient available balance and valid allocation amounts.',
      sourceAmountsChanged:
        'The draft amounts differ from the supplier original. Restore the original amounts before approval.',
      supplierCreditReviewRequired:
        'This supplier credit is retained as a reviewable draft. Supplier credit settlement is not supported here; approval and outgoing payment are blocked. Have accounting reconcile it against the original invoice.',
      title: 'Electronic invoices',
      description:
        'Validated invoice files and reviewed incoming invoices with retained original evidence.',
      import: 'Import XML or hybrid PDF',
      export: 'Validate, archive and download',
      validating: 'Validating…',
      loading: 'Loading…',
      retry: 'Retry',
      save: 'Save',
      search: 'Search invoices',
      outgoing: 'Outgoing archive',
      incoming: 'Incoming review',
      outgoingEmpty: 'Validate an invoice from its document details after finalization.',
      incomingEmpty: 'Import a supplier invoice to begin the review.',
      invoice: 'Invoice',
      format: 'Format',
      validation: 'Validation',
      files: 'Files',
      download: 'Download',
      xml: 'XML',
      reports: 'Validation reports',
      original: 'Original file',
      valid: 'Valid',
      supplier: 'Confirmed supplier',
      supplierMissing:
        'No existing supplier matches the source VAT identity. Maintain the supplier and reload the review.',
      supplierRequired: 'Select and confirm the supplier.',
      review: 'Review',
      imported: 'Draft created',
      status: 'Status',
      reviewNotice:
        'Review recipient, amounts, supplier and each order/receipt assignment. Import creates a draft; normal matching and approval remain required.',
      net: 'Net',
      tax: 'VAT',
      gross: 'Gross',
      allowances: 'Document discounts',
      charges: 'Document charges',
      openDraft: 'Open supplier invoice',
      select: 'Select explicitly',
      targetDraft: 'Target supplier draft',
      newDraft: 'Create a new draft',
      purchaseOrder: 'Purchase order',
      receipt: 'Posted goods receipt',
      line: 'Invoice line',
      noMapping: 'No assignment',
      createDraft: 'Confirm and create supplier draft',
      settings: 'Invoice settings and readiness',
      ready: 'Ready',
      notReady: 'Setup required',
      setupNotice:
        'Ask administration to install the official local validation package. Missing or invalid validators block export and import.',
      defaultFormat: 'Tenant default format',
      customerFormat: 'Customer invoice format',
      none: 'Ordinary PDF',
      sellerContact: 'Seller contact name',
      endpoint: 'Electronic address',
      endpointType: 'Electronic address scheme',
      email: 'Email',
      buyerReference: 'Buyer reference / routing ID',
      orderReference: 'Buyer order reference',
      receiptReference: 'Receipt reference',
      exemptionReason: 'Tax exemption reason',
      retention: 'Retention in years',
      frozen:
        'Export uses the seller, buyer, amounts and PDF layout frozen at finalization. Archived bytes remain identical.',
      finalizeHint:
        'Save invoice references before finalization. Finalize, validate and archive the invoice before email or portal delivery.',
      disabledHistory: 'The feature is disabled. Existing evidence remains readable.',
      frenchNotice:
        'Files and email do not provide French statutory platform transmission. Contract an approved platform; this ERP currently provides validated files for handover, not automatic platform submission.',
      costs:
        'Official validation artifacts are publicly available. Local Java, Ghostscript, veraPDF, storage and maintenance are required. Approved platforms may charge separate subscription or transaction fees.',
      limits:
        'Uploads: XML or a PDF/A-3 hybrid with one invoice attachment, maximum 10 MB. Minimum/basic-without-lines profiles cannot create a supplier draft. Validation runs locally with no external entity access.',
      error: 'The operation failed. Review the details and retry.',
      invalidInvoice: 'Invoice data is incomplete or inconsistent.',
      fileInvalid: 'Choose an XML invoice or hybrid PDF up to 10 MB.',
      duplicateInvoice:
        'An invoice with this supplier and number already exists. Review it; importing another draft is blocked.',
      recipientMismatch: 'The invoice recipient does not match this company.',
      duplicateOriginal:
        'This invoice XML already has a retained original. Open its existing review; another file cannot replace that evidence.',
      supplierMismatch: 'The selected supplier VAT identity does not match the original.',
      creditorAccountRequired: 'Maintain the supplier creditor account before import.',
      finalSnapshotRequired: 'Finalize the invoice before validating and sending it.',
      validatedArchiveRequired:
        'Validate and archive the selected invoice format from the document first.',
      historicalSnapshotMissing:
        'This historical invoice has no proven electronic snapshot. Automatic reconstruction is blocked.',
      validationRejected:
        'Independent validation rejected the invoice. Review the retained rule messages.',
      validatorUnavailable:
        'Local validators are unavailable or their checksums differ. Ask administration to repair the setup.',
      draftChanged: 'The target draft changed or is no longer editable. Reload and review.',
      previewChanged: 'The preview changed or has already been imported. Reload the review.',
      settingsInvalid: 'Check all settings and enable the chosen default format.',
      purchaseOrderMismatch: 'The order does not belong to this supplier or currency.',
      receiptMismatch: 'Select a posted receipt belonging to the same supplier and order line.',
      fileSize: 'Choose an XML invoice or hybrid PDF up to 10 MB.',
      malformedXml: 'Invoice data is incomplete or inconsistent.',
      missingValue: 'Invoice data is incomplete or inconsistent.',
      unsafeXml: 'Invoice data is incomplete or inconsistent.',
      unsupportedFormat: 'Invoice data is incomplete or inconsistent.',
      unsupportedType: 'Invoice data is incomplete or inconsistent.',
      utf8Required: 'Invoice data is incomplete or inconsistent.',
      xmlComplexity: 'Invoice data is incomplete or inconsistent.',
      invalidText: 'Invoice data is incomplete or inconsistent.',
      totalMismatch: 'Invoice data is incomplete or inconsistent.',
      invalidQuantity: 'Invoice data is incomplete or inconsistent.',
      invalidAllowance: 'Invoice data is incomplete or inconsistent.',
      duplicateTaxGroup: 'Invoice data is incomplete or inconsistent.',
      invalidTaxCategory: 'Invoice data is incomplete or inconsistent.',
      missingTaxGroup: 'Invoice data is incomplete or inconsistent.',
      buyerReferenceRequired: 'Buyer reference / routing ID',
      endpointRequired: 'Electronic address',
      routingRequired: 'Buyer reference / routing ID',
      identityIncomplete: 'Invoice data is incomplete or inconsistent.',
      formatDisabled: 'Check all settings and enable the chosen default format.',
      settingsChanged: 'The target draft changed or is no longer editable. Reload and review.',
      lineMappingInvalid: 'Select a posted receipt belonging to the same supplier and order line.',
      platformConnectorUnavailable:
        'Files and email do not provide French statutory platform transmission. Contract an approved platform; this ERP currently provides validated files for handover, not automatic platform submission.',
      platformContractRequired:
        'Files and email do not provide French statutory platform transmission. Contract an approved platform; this ERP currently provides validated files for handover, not automatic platform submission.',
      defaultFormatInvalid: 'Check all settings and enable the chosen default format.',
      fileRequired: 'Choose an XML invoice or hybrid PDF up to 10 MB.',
      pdfInvalid: 'Choose an XML invoice or hybrid PDF up to 10 MB.',
      invoiceAttachmentRequired: 'Choose an XML invoice or hybrid PDF up to 10 MB.',
      pdfConversionFailed:
        'Local validators are unavailable or their checksums differ. Ask administration to repair the setup.',
      pdfValidationRejected:
        'Independent validation rejected the invoice. Review the retained rule messages.',
      validatorFailed:
        'Local validators are unavailable or their checksums differ. Ask administration to repair the setup.',
      snapshotChecksumMismatch:
        'Independent validation rejected the invoice. Review the retained rule messages.',
      archiveChecksumMismatch:
        'Independent validation rejected the invoice. Review the retained rule messages.',
      attachmentChecksumMismatch:
        'Independent validation rejected the invoice. Review the retained rule messages.',
      snapshotChanged: 'The target draft changed or is no longer editable. Reload and review.',
      formats: {
        FACTUR_X: 'ZUGFeRD / Factur-X (PDF/A-3)',
        XRECHNUNG_CII: 'XRechnung (CII XML)',
        XRECHNUNG_UBL: 'XRechnung (UBL XML)',
      },
      checks: {
        'Official XSD and Schematron artifacts': 'Official XSD and Schematron artifacts',
        Java: 'Java',
        Ghostscript: 'Ghostscript',
        veraPDF: 'veraPDF',
        'ICC archival colour profile': 'ICC archival colour profile',
      },
    },
  },
  de: {
    electronicInvoices: {
      prepaid: 'Bereits gezahlter Betrag',
      rounding: 'Zahlbetragsrundung',
      due: 'Noch zu zahlen',
      advancePayment: 'Vorhandene Vorauszahlung',
      advanceAmount: 'Zuordnungsbetrag',
      removeAdvance: 'Zuordnung entfernen',
      linkAdvance: 'Vorauszahlung zuordnen',
      reconcileAdvances: 'Vorauszahlungen abgleichen',
      prepaymentNotice:
        'Wählen Sie tatsächlich vorhandene Lieferantenzahlungen. Die Zuordnung erzeugt keine Zahlung. Nicht abgeglichene Vorauszahlungen sperren die Freigabe; die Zuordnung ist später in dieser Prüfung möglich.',
      prepaymentPermission:
        'Eine Buchhaltungsrolle mit Banklese- und Buchungsrechten muss die tatsächlichen Zahlungen abgleichen.',
      prepaymentUnreconciled:
        'Der bereits gezahlte Lieferantenbetrag ist nicht mit tatsächlichen Zahlungen abgeglichen. Öffnen Sie die E-Rechnungsprüfung erneut und ordnen Sie die vorhandenen Vorauszahlungen vor der Freigabe zu.',
      prepaymentInvalid:
        'Wählen Sie aktive Zahlungen desselben Lieferanten und derselben Währung mit ausreichendem verfügbarem Betrag und gültigen Zuordnungsbeträgen.',
      sourceAmountsChanged:
        'Die Entwurfsbeträge weichen vom Lieferantenoriginal ab. Stellen Sie vor der Freigabe die Originalbeträge wieder her.',
      supplierCreditReviewRequired:
        'Diese Lieferantengutschrift bleibt als prüfbarer Entwurf erhalten. Der finanzielle Ausgleich von Lieferantengutschriften wird hier nicht unterstützt; Freigabe und ausgehende Zahlung sind gesperrt. Lassen Sie die Buchhaltung die Zuordnung zur Originalrechnung prüfen.',
      title: 'E-Rechnungen',
      description:
        'Geprüfte Rechnungsdateien und kontrollierte Eingangsrechnungen mit aufbewahrten Originalnachweisen.',
      import: 'XML oder Hybrid-PDF importieren',
      export: 'Prüfen, archivieren und herunterladen',
      validating: 'Wird geprüft…',
      loading: 'Wird geladen…',
      retry: 'Erneut versuchen',
      save: 'Speichern',
      search: 'Rechnungen suchen',
      outgoing: 'Ausgangsarchiv',
      incoming: 'Eingangsprüfung',
      outgoingEmpty: 'Prüfen Sie eine Rechnung nach der Festschreibung in den Belegdetails.',
      incomingEmpty: 'Importieren Sie eine Lieferantenrechnung für die Prüfung.',
      invoice: 'Rechnung',
      format: 'Format',
      validation: 'Prüfung',
      files: 'Dateien',
      download: 'Herunterladen',
      xml: 'XML',
      reports: 'Prüfberichte',
      original: 'Originaldatei',
      valid: 'Gültig',
      supplier: 'Bestätigter Lieferant',
      supplierMissing:
        'Kein vorhandener Lieferant passt zur USt-Identität des Originals. Pflegen Sie den Lieferanten und laden Sie die Prüfung neu.',
      supplierRequired: 'Wählen und bestätigen Sie den Lieferanten.',
      review: 'Prüfen',
      imported: 'Entwurf angelegt',
      status: 'Status',
      reviewNotice:
        'Prüfen Sie Empfänger, Beträge, Lieferant und jede Bestell-/Wareneingangszuordnung. Der Import erstellt einen Entwurf; regulärer Abgleich und Freigabe bleiben erforderlich.',
      net: 'Netto',
      tax: 'Umsatzsteuer',
      gross: 'Brutto',
      allowances: 'Belegrabatte',
      charges: 'Belegzuschläge',
      openDraft: 'Lieferantenrechnung öffnen',
      select: 'Bewusst auswählen',
      targetDraft: 'Zielentwurf beim Lieferanten',
      newDraft: 'Neuen Entwurf anlegen',
      purchaseOrder: 'Bestellung',
      receipt: 'Gebuchter Wareneingang',
      line: 'Rechnungsposition',
      noMapping: 'Keine Zuordnung',
      createDraft: 'Bestätigen und Lieferantenentwurf erstellen',
      settings: 'Rechnungseinstellungen und Bereitschaft',
      ready: 'Bereit',
      notReady: 'Einrichtung erforderlich',
      setupNotice:
        'Lassen Sie das offizielle lokale Prüfpaket administrativ installieren. Fehlende oder ungültige Prüfer sperren Export und Import.',
      defaultFormat: 'Mandanten-Standardformat',
      customerFormat: 'Rechnungsformat des Kunden',
      none: 'Normales PDF',
      sellerContact: 'Name des Verkäuferkontakts',
      endpoint: 'Elektronische Adresse',
      endpointType: 'Adressschema',
      email: 'E-Mail',
      buyerReference: 'Käuferreferenz / Leitweg-ID',
      orderReference: 'Bestellreferenz des Käufers',
      receiptReference: 'Wareneingangsreferenz',
      exemptionReason: 'Steuerbefreiungsgrund',
      retention: 'Aufbewahrung in Jahren',
      frozen:
        'Der Export verwendet die bei Festschreibung eingefrorenen Verkäufer-, Käufer-, Betrags- und PDF-Daten. Archivierte Bytes bleiben identisch.',
      finalizeHint:
        'Speichern Sie Rechnungsreferenzen vor der Festschreibung. Schreiben Sie die Rechnung fest, prüfen und archivieren Sie sie vor E-Mail- oder Portalzustellung.',
      disabledHistory: 'Die Funktion ist deaktiviert. Vorhandene Nachweise bleiben lesbar.',
      frenchNotice:
        'Dateien und E-Mail ersetzen keine französische Plattformübermittlung. Beauftragen Sie eine zugelassene Plattform; dieses ERP liefert derzeit geprüfte Dateien zur Übergabe, keine automatische Plattformmeldung.',
      costs:
        'Offizielle Prüfdateien sind öffentlich verfügbar. Lokales Java, Ghostscript, veraPDF, Speicher und Wartung werden benötigt. Zugelassene Plattformen können separate Abo- oder Transaktionskosten berechnen.',
      limits:
        'Uploads: XML oder PDF/A-3-Hybrid mit genau einem Rechnungsanhang, maximal 10 MB. Minimum-/Basic-ohne-Positionen-Profile erstellen keinen Lieferantenentwurf. Die Prüfung läuft lokal ohne externe Entitäten.',
      error: 'Der Vorgang ist fehlgeschlagen. Prüfen Sie die Angaben und wiederholen Sie ihn.',
      invalidInvoice: 'Rechnungsdaten sind unvollständig oder widersprüchlich.',
      fileInvalid: 'Wählen Sie eine XML-Rechnung oder Hybrid-PDF bis 10 MB.',
      duplicateInvoice:
        'Für diesen Lieferanten und diese Nummer besteht bereits eine Rechnung. Prüfen Sie sie; ein weiterer Entwurf wird gesperrt.',
      recipientMismatch: 'Der Rechnungsempfänger passt nicht zu diesem Unternehmen.',
      duplicateOriginal:
        'Zu diesen Rechnungsdaten ist bereits ein Original archiviert. Öffnen Sie die vorhandene Prüfung; eine andere Datei kann diesen Nachweis nicht ersetzen.',
      supplierMismatch:
        'Die USt-Identität des ausgewählten Lieferanten stimmt nicht mit dem Original überein.',
      creditorAccountRequired: 'Pflegen Sie vor dem Import das Kreditorenkonto des Lieferanten.',
      finalSnapshotRequired: 'Schreiben Sie die Rechnung vor Prüfung und Versand fest.',
      validatedArchiveRequired:
        'Prüfen und archivieren Sie zuerst das ausgewählte Rechnungsformat im Beleg.',
      historicalSnapshotMissing:
        'Dieser historische Beleg besitzt keinen nachgewiesenen E-Rechnungsstand. Automatische Rekonstruktion ist gesperrt.',
      validationRejected:
        'Die unabhängige Prüfung hat die Rechnung abgelehnt. Prüfen Sie die Regelmeldungen.',
      validatorUnavailable:
        'Lokale Prüfer fehlen oder ihre Prüfsummen weichen ab. Lassen Sie die Einrichtung administrativ reparieren.',
      draftChanged:
        'Der Zielentwurf wurde geändert oder ist nicht mehr bearbeitbar. Laden und prüfen Sie ihn neu.',
      previewChanged:
        'Die Vorschau wurde geändert oder bereits importiert. Laden Sie die Prüfung neu.',
      settingsInvalid:
        'Prüfen Sie alle Einstellungen und aktivieren Sie das gewählte Standardformat.',
      purchaseOrderMismatch:
        'Die Bestellung gehört nicht zu diesem Lieferanten oder dieser Währung.',
      receiptMismatch:
        'Wählen Sie einen gebuchten Wareneingang desselben Lieferanten und derselben Bestellposition.',
      fileSize: 'Wählen Sie eine XML-Rechnung oder Hybrid-PDF bis 10 MB.',
      malformedXml: 'Rechnungsdaten sind unvollständig oder widersprüchlich.',
      missingValue: 'Rechnungsdaten sind unvollständig oder widersprüchlich.',
      unsafeXml: 'Rechnungsdaten sind unvollständig oder widersprüchlich.',
      unsupportedFormat: 'Rechnungsdaten sind unvollständig oder widersprüchlich.',
      unsupportedType: 'Rechnungsdaten sind unvollständig oder widersprüchlich.',
      utf8Required: 'Rechnungsdaten sind unvollständig oder widersprüchlich.',
      xmlComplexity: 'Rechnungsdaten sind unvollständig oder widersprüchlich.',
      invalidText: 'Rechnungsdaten sind unvollständig oder widersprüchlich.',
      totalMismatch: 'Rechnungsdaten sind unvollständig oder widersprüchlich.',
      invalidQuantity: 'Rechnungsdaten sind unvollständig oder widersprüchlich.',
      invalidAllowance: 'Rechnungsdaten sind unvollständig oder widersprüchlich.',
      duplicateTaxGroup: 'Rechnungsdaten sind unvollständig oder widersprüchlich.',
      invalidTaxCategory: 'Rechnungsdaten sind unvollständig oder widersprüchlich.',
      missingTaxGroup: 'Rechnungsdaten sind unvollständig oder widersprüchlich.',
      buyerReferenceRequired: 'Käuferreferenz / Leitweg-ID',
      endpointRequired: 'Elektronische Adresse',
      routingRequired: 'Käuferreferenz / Leitweg-ID',
      identityIncomplete: 'Rechnungsdaten sind unvollständig oder widersprüchlich.',
      formatDisabled:
        'Prüfen Sie alle Einstellungen und aktivieren Sie das gewählte Standardformat.',
      settingsChanged:
        'Der Zielentwurf wurde geändert oder ist nicht mehr bearbeitbar. Laden und prüfen Sie ihn neu.',
      lineMappingInvalid:
        'Wählen Sie einen gebuchten Wareneingang desselben Lieferanten und derselben Bestellposition.',
      platformConnectorUnavailable:
        'Dateien und E-Mail ersetzen keine französische Plattformübermittlung. Beauftragen Sie eine zugelassene Plattform; dieses ERP liefert derzeit geprüfte Dateien zur Übergabe, keine automatische Plattformmeldung.',
      platformContractRequired:
        'Dateien und E-Mail ersetzen keine französische Plattformübermittlung. Beauftragen Sie eine zugelassene Plattform; dieses ERP liefert derzeit geprüfte Dateien zur Übergabe, keine automatische Plattformmeldung.',
      defaultFormatInvalid:
        'Prüfen Sie alle Einstellungen und aktivieren Sie das gewählte Standardformat.',
      fileRequired: 'Wählen Sie eine XML-Rechnung oder Hybrid-PDF bis 10 MB.',
      pdfInvalid: 'Wählen Sie eine XML-Rechnung oder Hybrid-PDF bis 10 MB.',
      invoiceAttachmentRequired: 'Wählen Sie eine XML-Rechnung oder Hybrid-PDF bis 10 MB.',
      pdfConversionFailed:
        'Lokale Prüfer fehlen oder ihre Prüfsummen weichen ab. Lassen Sie die Einrichtung administrativ reparieren.',
      pdfValidationRejected:
        'Die unabhängige Prüfung hat die Rechnung abgelehnt. Prüfen Sie die Regelmeldungen.',
      validatorFailed:
        'Lokale Prüfer fehlen oder ihre Prüfsummen weichen ab. Lassen Sie die Einrichtung administrativ reparieren.',
      snapshotChecksumMismatch:
        'Die unabhängige Prüfung hat die Rechnung abgelehnt. Prüfen Sie die Regelmeldungen.',
      archiveChecksumMismatch:
        'Die unabhängige Prüfung hat die Rechnung abgelehnt. Prüfen Sie die Regelmeldungen.',
      attachmentChecksumMismatch:
        'Die unabhängige Prüfung hat die Rechnung abgelehnt. Prüfen Sie die Regelmeldungen.',
      snapshotChanged:
        'Der Zielentwurf wurde geändert oder ist nicht mehr bearbeitbar. Laden und prüfen Sie ihn neu.',
      formats: {
        FACTUR_X: 'ZUGFeRD / Factur-X (PDF/A-3)',
        XRECHNUNG_CII: 'XRechnung (CII XML)',
        XRECHNUNG_UBL: 'XRechnung (UBL XML)',
      },
      checks: {
        'Official XSD and Schematron artifacts': 'Offizielle XSD- und Schematron-Prüfdateien',
        Java: 'Java',
        Ghostscript: 'Ghostscript',
        veraPDF: 'veraPDF',
        'ICC archival colour profile': 'ICC-Farbprofil für das Archiv',
      },
    },
  },
  fr: {
    electronicInvoices: {
      prepaid: 'Montant déjà payé',
      rounding: 'Arrondi du montant à régler',
      due: 'Montant restant dû',
      advancePayment: 'Acompte existant',
      advanceAmount: 'Montant à affecter',
      removeAdvance: 'Retirer l’affectation',
      linkAdvance: 'Affecter un acompte',
      reconcileAdvances: 'Rapprocher les acomptes',
      prepaymentNotice:
        'Sélectionnez des paiements fournisseur réellement existants. L’affectation ne crée aucun paiement. Les acomptes non rapprochés bloquent l’approbation ; vous pouvez les affecter ultérieurement dans cette revue.',
      prepaymentPermission:
        'Un comptable disposant des droits de lecture et de comptabilisation bancaire doit rapprocher les paiements réels.',
      prepaymentUnreconciled:
        'Le montant prépayé du fournisseur n’est pas rapproché de paiements réels. Rouvrez la revue de facture électronique et affectez les acomptes existants avant l’approbation.',
      prepaymentInvalid:
        'Sélectionnez des paiements actifs du même fournisseur et dans la même devise, avec un solde disponible suffisant et des montants d’affectation valides.',
      sourceAmountsChanged:
        'Les montants du brouillon diffèrent de l’original fournisseur. Rétablissez les montants d’origine avant l’approbation.',
      supplierCreditReviewRequired:
        'Cet avoir fournisseur est conservé comme brouillon consultable. Le règlement des avoirs fournisseur n’est pas pris en charge ici ; l’approbation et le paiement sortant sont bloqués. Demandez à la comptabilité de le rapprocher de la facture originale.',
      title: 'Factures électroniques',
      description:
        'Fichiers de facture validés et factures entrantes contrôlées avec conservation des originaux.',
      import: 'Importer XML ou PDF hybride',
      export: 'Valider, archiver et télécharger',
      validating: 'Validation…',
      loading: 'Chargement…',
      retry: 'Réessayer',
      save: 'Enregistrer',
      search: 'Rechercher des factures',
      outgoing: 'Archives sortantes',
      incoming: 'Contrôle des factures reçues',
      outgoingEmpty: 'Validez une facture depuis son détail après finalisation.',
      incomingEmpty: 'Importez une facture fournisseur pour commencer le contrôle.',
      invoice: 'Facture',
      format: 'Format',
      validation: 'Validation',
      files: 'Fichiers',
      download: 'Télécharger',
      xml: 'XML',
      reports: 'Rapports de validation',
      original: 'Fichier original',
      valid: 'Valide',
      supplier: 'Fournisseur confirmé',
      supplierMissing:
        'Aucun fournisseur existant ne correspond à l’identité TVA source. Renseignez le fournisseur et rechargez le contrôle.',
      supplierRequired: 'Choisissez et confirmez le fournisseur.',
      review: 'Contrôler',
      imported: 'Brouillon créé',
      status: 'État',
      reviewNotice:
        'Vérifiez destinataire, montants, fournisseur et chaque affectation commande/réception. L’import crée un brouillon ; rapprochement et approbation habituels restent obligatoires.',
      net: 'Hors taxe',
      tax: 'TVA',
      gross: 'TTC',
      allowances: 'Remises globales',
      charges: 'Frais globaux',
      openDraft: 'Ouvrir la facture fournisseur',
      select: 'Choisir explicitement',
      targetDraft: 'Brouillon fournisseur cible',
      newDraft: 'Créer un nouveau brouillon',
      purchaseOrder: 'Commande fournisseur',
      receipt: 'Réception comptabilisée',
      line: 'Ligne de facture',
      noMapping: 'Sans affectation',
      createDraft: 'Confirmer et créer le brouillon fournisseur',
      settings: 'Paramètres et préparation',
      ready: 'Prêt',
      notReady: 'Configuration requise',
      setupNotice:
        'Demandez à l’administration d’installer le paquet officiel de validation locale. Les validateurs absents ou invalides bloquent export et import.',
      defaultFormat: 'Format par défaut de l’entreprise',
      customerFormat: 'Format du client',
      none: 'PDF ordinaire',
      sellerContact: 'Nom du contact vendeur',
      endpoint: 'Adresse électronique',
      endpointType: 'Schéma d’adresse',
      email: 'Courriel',
      buyerReference: 'Référence acheteur / routage',
      orderReference: 'Référence de commande acheteur',
      receiptReference: 'Référence de réception',
      exemptionReason: 'Motif d’exonération',
      retention: 'Conservation en années',
      frozen:
        'L’export utilise vendeur, acheteur, montants et mise en page figés lors de la finalisation. Les octets archivés restent identiques.',
      finalizeHint:
        'Enregistrez les références avant finalisation. Finalisez, validez et archivez avant l’envoi par courriel ou portail.',
      disabledHistory: 'La fonction est désactivée. Les preuves existantes restent consultables.',
      frenchNotice:
        'Les fichiers et courriels ne remplacent pas la transmission légale française. Contractez une plateforme agréée ; cet ERP fournit actuellement des fichiers validés à remettre, sans dépôt automatique.',
      costs:
        'Les règles officielles sont publiques. Java, Ghostscript, veraPDF, stockage et maintenance locaux sont requis. Les plateformes agréées peuvent facturer abonnements ou transactions.',
      limits:
        'Imports : XML ou PDF/A-3 hybride contenant une seule facture, 10 Mo maximum. Les profils minimum/basic sans lignes ne créent pas de brouillon fournisseur. Validation locale sans entités externes.',
      error: 'L’opération a échoué. Vérifiez les détails et réessayez.',
      invalidInvoice: 'Les données de facture sont incomplètes ou incohérentes.',
      fileInvalid: 'Choisissez une facture XML ou un PDF hybride de 10 Mo maximum.',
      duplicateInvoice:
        'Une facture de ce fournisseur avec ce numéro existe déjà. Vérifiez-la ; un autre brouillon est bloqué.',
      recipientMismatch: 'Le destinataire ne correspond pas à cette entreprise.',
      duplicateOriginal:
        'Cet XML possède déjà un original conservé. Ouvrez son contrôle existant ; un autre fichier ne peut pas remplacer cette preuve.',
      supplierMismatch: 'L’identité TVA du fournisseur choisi ne correspond pas à l’original.',
      creditorAccountRequired: 'Renseignez le compte fournisseur avant import.',
      finalSnapshotRequired: 'Finalisez la facture avant validation et envoi.',
      validatedArchiveRequired: 'Validez et archivez d’abord le format choisi depuis la facture.',
      historicalSnapshotMissing:
        'Cette ancienne facture ne possède pas d’instantané électronique prouvé. La reconstruction automatique est bloquée.',
      validationRejected:
        'La validation indépendante a rejeté la facture. Vérifiez les messages de règles.',
      validatorUnavailable:
        'Les validateurs locaux sont indisponibles ou leurs empreintes diffèrent. Demandez à l’administration de réparer l’installation.',
      draftChanged: 'Le brouillon cible a changé ou n’est plus modifiable. Rechargez et vérifiez.',
      previewChanged: 'L’aperçu a changé ou a déjà été importé. Rechargez le contrôle.',
      settingsInvalid: 'Vérifiez tous les paramètres et activez le format par défaut choisi.',
      purchaseOrderMismatch: 'La commande ne correspond pas à ce fournisseur ou à cette devise.',
      receiptMismatch:
        'Choisissez une réception comptabilisée du même fournisseur et de la même ligne de commande.',
      fileSize: 'Choisissez une facture XML ou un PDF hybride de 10 Mo maximum.',
      malformedXml: 'Les données de facture sont incomplètes ou incohérentes.',
      missingValue: 'Les données de facture sont incomplètes ou incohérentes.',
      unsafeXml: 'Les données de facture sont incomplètes ou incohérentes.',
      unsupportedFormat: 'Les données de facture sont incomplètes ou incohérentes.',
      unsupportedType: 'Les données de facture sont incomplètes ou incohérentes.',
      utf8Required: 'Les données de facture sont incomplètes ou incohérentes.',
      xmlComplexity: 'Les données de facture sont incomplètes ou incohérentes.',
      invalidText: 'Les données de facture sont incomplètes ou incohérentes.',
      totalMismatch: 'Les données de facture sont incomplètes ou incohérentes.',
      invalidQuantity: 'Les données de facture sont incomplètes ou incohérentes.',
      invalidAllowance: 'Les données de facture sont incomplètes ou incohérentes.',
      duplicateTaxGroup: 'Les données de facture sont incomplètes ou incohérentes.',
      invalidTaxCategory: 'Les données de facture sont incomplètes ou incohérentes.',
      missingTaxGroup: 'Les données de facture sont incomplètes ou incohérentes.',
      buyerReferenceRequired: 'Référence acheteur / routage',
      endpointRequired: 'Adresse électronique',
      routingRequired: 'Référence acheteur / routage',
      identityIncomplete: 'Les données de facture sont incomplètes ou incohérentes.',
      formatDisabled: 'Vérifiez tous les paramètres et activez le format par défaut choisi.',
      settingsChanged:
        'Le brouillon cible a changé ou n’est plus modifiable. Rechargez et vérifiez.',
      lineMappingInvalid:
        'Choisissez une réception comptabilisée du même fournisseur et de la même ligne de commande.',
      platformConnectorUnavailable:
        'Les fichiers et courriels ne remplacent pas la transmission légale française. Contractez une plateforme agréée ; cet ERP fournit actuellement des fichiers validés à remettre, sans dépôt automatique.',
      platformContractRequired:
        'Les fichiers et courriels ne remplacent pas la transmission légale française. Contractez une plateforme agréée ; cet ERP fournit actuellement des fichiers validés à remettre, sans dépôt automatique.',
      defaultFormatInvalid: 'Vérifiez tous les paramètres et activez le format par défaut choisi.',
      fileRequired: 'Choisissez une facture XML ou un PDF hybride de 10 Mo maximum.',
      pdfInvalid: 'Choisissez une facture XML ou un PDF hybride de 10 Mo maximum.',
      invoiceAttachmentRequired: 'Choisissez une facture XML ou un PDF hybride de 10 Mo maximum.',
      pdfConversionFailed:
        'Les validateurs locaux sont indisponibles ou leurs empreintes diffèrent. Demandez à l’administration de réparer l’installation.',
      pdfValidationRejected:
        'La validation indépendante a rejeté la facture. Vérifiez les messages de règles.',
      validatorFailed:
        'Les validateurs locaux sont indisponibles ou leurs empreintes diffèrent. Demandez à l’administration de réparer l’installation.',
      snapshotChecksumMismatch:
        'La validation indépendante a rejeté la facture. Vérifiez les messages de règles.',
      archiveChecksumMismatch:
        'La validation indépendante a rejeté la facture. Vérifiez les messages de règles.',
      attachmentChecksumMismatch:
        'La validation indépendante a rejeté la facture. Vérifiez les messages de règles.',
      snapshotChanged:
        'Le brouillon cible a changé ou n’est plus modifiable. Rechargez et vérifiez.',
      formats: {
        FACTUR_X: 'ZUGFeRD / Factur-X (PDF/A-3)',
        XRECHNUNG_CII: 'XRechnung (CII XML)',
        XRECHNUNG_UBL: 'XRechnung (UBL XML)',
      },
      checks: {
        'Official XSD and Schematron artifacts': 'Règles XSD et Schematron officielles',
        Java: 'Java',
        Ghostscript: 'Ghostscript',
        veraPDF: 'veraPDF',
        'ICC archival colour profile': 'Profil couleur ICC pour archives',
      },
    },
  },
  es: {
    electronicInvoices: {
      prepaid: 'Importe ya pagado',
      rounding: 'Redondeo del pago',
      due: 'Importe pendiente',
      advancePayment: 'Anticipo existente',
      advanceAmount: 'Importe a asignar',
      removeAdvance: 'Quitar asignación',
      linkAdvance: 'Asignar un anticipo',
      reconcileAdvances: 'Conciliar anticipos',
      prepaymentNotice:
        'Seleccione pagos reales del proveedor ya existentes. La asignación no crea ningún pago. Los anticipos sin conciliar bloquean la aprobación; puede asignarlos más adelante en esta revisión.',
      prepaymentPermission:
        'Un contable con permisos de lectura y contabilización bancaria debe conciliar los pagos reales.',
      prepaymentUnreconciled:
        'El importe prepagado del proveedor no está conciliado con pagos reales. Abra de nuevo la revisión de la factura electrónica y asigne los anticipos existentes antes de aprobar.',
      prepaymentInvalid:
        'Seleccione pagos activos del mismo proveedor y moneda, con saldo disponible suficiente e importes de asignación válidos.',
      sourceAmountsChanged:
        'Los importes del borrador difieren del original del proveedor. Restaure los importes originales antes de aprobar.',
      supplierCreditReviewRequired:
        'Este abono del proveedor se conserva como borrador revisable. Aquí no se admite la liquidación de abonos de proveedores; la aprobación y el pago saliente están bloqueados. Pida a contabilidad que lo concilie con la factura original.',
      title: 'Facturas electrónicas',
      description:
        'Archivos de factura validados y facturas recibidas revisadas con conservación de originales.',
      import: 'Importar XML o PDF híbrido',
      export: 'Validar, archivar y descargar',
      validating: 'Validando…',
      loading: 'Cargando…',
      retry: 'Reintentar',
      save: 'Guardar',
      search: 'Buscar facturas',
      outgoing: 'Archivo de emitidas',
      incoming: 'Revisión de recibidas',
      outgoingEmpty: 'Valide una factura desde su detalle después de finalizarla.',
      incomingEmpty: 'Importe una factura de proveedor para iniciar la revisión.',
      invoice: 'Factura',
      format: 'Formato',
      validation: 'Validación',
      files: 'Archivos',
      download: 'Descargar',
      xml: 'XML',
      reports: 'Informes de validación',
      original: 'Archivo original',
      valid: 'Válida',
      supplier: 'Proveedor confirmado',
      supplierMissing:
        'Ningún proveedor existente coincide con la identidad IVA original. Registre el proveedor y recargue la revisión.',
      supplierRequired: 'Seleccione y confirme el proveedor.',
      review: 'Revisar',
      imported: 'Borrador creado',
      status: 'Estado',
      reviewNotice:
        'Revise destinatario, importes, proveedor y cada asignación a pedido/recepción. La importación crea un borrador; se mantienen la conciliación y aprobación habituales.',
      net: 'Neto',
      tax: 'IVA',
      gross: 'Total',
      allowances: 'Descuentos globales',
      charges: 'Cargos globales',
      openDraft: 'Abrir factura del proveedor',
      select: 'Seleccionar expresamente',
      targetDraft: 'Borrador de proveedor de destino',
      newDraft: 'Crear un borrador nuevo',
      purchaseOrder: 'Pedido de compra',
      receipt: 'Recepción contabilizada',
      line: 'Línea de factura',
      noMapping: 'Sin asignación',
      createDraft: 'Confirmar y crear borrador del proveedor',
      settings: 'Configuración y preparación',
      ready: 'Preparado',
      notReady: 'Requiere configuración',
      setupNotice:
        'Solicite a administración instalar el paquete oficial de validación local. Los validadores ausentes o inválidos bloquean la exportación e importación.',
      defaultFormat: 'Formato predeterminado de la empresa',
      customerFormat: 'Formato de factura del cliente',
      none: 'PDF ordinario',
      sellerContact: 'Nombre del contacto del vendedor',
      endpoint: 'Dirección electrónica',
      endpointType: 'Esquema de dirección',
      email: 'Correo electrónico',
      buyerReference: 'Referencia del comprador / enrutamiento',
      orderReference: 'Referencia del pedido del comprador',
      receiptReference: 'Referencia de recepción',
      exemptionReason: 'Motivo de exención',
      retention: 'Conservación en años',
      frozen:
        'La exportación usa vendedor, comprador, importes y diseño PDF congelados al finalizar. Los bytes archivados permanecen idénticos.',
      finalizeHint:
        'Guarde referencias antes de finalizar. Finalice, valide y archive antes de enviar por correo o portal.',
      disabledHistory: 'La función está desactivada. Las evidencias existentes siguen disponibles.',
      frenchNotice:
        'Los archivos y el correo no sustituyen la transmisión legal francesa. Contrate una plataforma autorizada; este ERP entrega archivos validados para su transferencia, sin envío automático.',
      costs:
        'Las reglas oficiales son públicas. Se requieren Java, Ghostscript, veraPDF, almacenamiento y mantenimiento locales. Las plataformas autorizadas pueden cobrar suscripciones o transacciones.',
      limits:
        'Cargas: XML o PDF/A-3 híbrido con un único adjunto de factura, máximo 10 MB. Los perfiles mínimos/basic sin líneas no crean borradores. Validación local sin acceso a entidades externas.',
      error: 'La operación falló. Revise los detalles y reintente.',
      invalidInvoice: 'Los datos de la factura son incompletos o incoherentes.',
      fileInvalid: 'Elija factura XML o PDF híbrido de hasta 10 MB.',
      duplicateInvoice:
        'Ya existe una factura de este proveedor con este número. Revísela; se bloquea otro borrador.',
      recipientMismatch: 'El destinatario no coincide con esta empresa.',
      duplicateOriginal:
        'Este XML ya tiene un original conservado. Abra su revisión existente; otro archivo no puede reemplazar esa prueba.',
      supplierMismatch: 'La identidad IVA del proveedor seleccionado no coincide con el original.',
      creditorAccountRequired: 'Registre la cuenta contable del proveedor antes de importar.',
      finalSnapshotRequired: 'Finalice la factura antes de validarla y enviarla.',
      validatedArchiveRequired:
        'Valide y archive primero el formato seleccionado desde la factura.',
      historicalSnapshotMissing:
        'Esta factura histórica no tiene una captura electrónica acreditada. Se bloquea la reconstrucción automática.',
      validationRejected:
        'La validación independiente rechazó la factura. Revise los mensajes de reglas.',
      validatorUnavailable:
        'Los validadores locales no están disponibles o difieren sus sumas. Solicite reparar la instalación.',
      draftChanged: 'El borrador cambió o ya no se puede editar. Recargue y revise.',
      previewChanged: 'La vista previa cambió o ya se importó. Recargue la revisión.',
      settingsInvalid: 'Revise toda la configuración y active el formato predeterminado elegido.',
      purchaseOrderMismatch: 'El pedido no corresponde a este proveedor o moneda.',
      receiptMismatch: 'Seleccione recepción contabilizada del mismo proveedor y línea de pedido.',
      fileSize: 'Elija factura XML o PDF híbrido de hasta 10 MB.',
      malformedXml: 'Los datos de la factura son incompletos o incoherentes.',
      missingValue: 'Los datos de la factura son incompletos o incoherentes.',
      unsafeXml: 'Los datos de la factura son incompletos o incoherentes.',
      unsupportedFormat: 'Los datos de la factura son incompletos o incoherentes.',
      unsupportedType: 'Los datos de la factura son incompletos o incoherentes.',
      utf8Required: 'Los datos de la factura son incompletos o incoherentes.',
      xmlComplexity: 'Los datos de la factura son incompletos o incoherentes.',
      invalidText: 'Los datos de la factura son incompletos o incoherentes.',
      totalMismatch: 'Los datos de la factura son incompletos o incoherentes.',
      invalidQuantity: 'Los datos de la factura son incompletos o incoherentes.',
      invalidAllowance: 'Los datos de la factura son incompletos o incoherentes.',
      duplicateTaxGroup: 'Los datos de la factura son incompletos o incoherentes.',
      invalidTaxCategory: 'Los datos de la factura son incompletos o incoherentes.',
      missingTaxGroup: 'Los datos de la factura son incompletos o incoherentes.',
      buyerReferenceRequired: 'Referencia del comprador / enrutamiento',
      endpointRequired: 'Dirección electrónica',
      routingRequired: 'Referencia del comprador / enrutamiento',
      identityIncomplete: 'Los datos de la factura son incompletos o incoherentes.',
      formatDisabled: 'Revise toda la configuración y active el formato predeterminado elegido.',
      settingsChanged: 'El borrador cambió o ya no se puede editar. Recargue y revise.',
      lineMappingInvalid:
        'Seleccione recepción contabilizada del mismo proveedor y línea de pedido.',
      platformConnectorUnavailable:
        'Los archivos y el correo no sustituyen la transmisión legal francesa. Contrate una plataforma autorizada; este ERP entrega archivos validados para su transferencia, sin envío automático.',
      platformContractRequired:
        'Los archivos y el correo no sustituyen la transmisión legal francesa. Contrate una plataforma autorizada; este ERP entrega archivos validados para su transferencia, sin envío automático.',
      defaultFormatInvalid:
        'Revise toda la configuración y active el formato predeterminado elegido.',
      fileRequired: 'Elija factura XML o PDF híbrido de hasta 10 MB.',
      pdfInvalid: 'Elija factura XML o PDF híbrido de hasta 10 MB.',
      invoiceAttachmentRequired: 'Elija factura XML o PDF híbrido de hasta 10 MB.',
      pdfConversionFailed:
        'Los validadores locales no están disponibles o difieren sus sumas. Solicite reparar la instalación.',
      pdfValidationRejected:
        'La validación independiente rechazó la factura. Revise los mensajes de reglas.',
      validatorFailed:
        'Los validadores locales no están disponibles o difieren sus sumas. Solicite reparar la instalación.',
      snapshotChecksumMismatch:
        'La validación independiente rechazó la factura. Revise los mensajes de reglas.',
      archiveChecksumMismatch:
        'La validación independiente rechazó la factura. Revise los mensajes de reglas.',
      attachmentChecksumMismatch:
        'La validación independiente rechazó la factura. Revise los mensajes de reglas.',
      snapshotChanged: 'El borrador cambió o ya no se puede editar. Recargue y revise.',
      formats: {
        FACTUR_X: 'ZUGFeRD / Factur-X (PDF/A-3)',
        XRECHNUNG_CII: 'XRechnung (CII XML)',
        XRECHNUNG_UBL: 'XRechnung (UBL XML)',
      },
      checks: {
        'Official XSD and Schematron artifacts': 'Reglas XSD y Schematron oficiales',
        Java: 'Java',
        Ghostscript: 'Ghostscript',
        veraPDF: 'veraPDF',
        'ICC archival colour profile': 'Perfil de color ICC para archivo',
      },
    },
  },
  tr: {
    electronicInvoices: {
      prepaid: 'Önceden ödenen tutar',
      rounding: 'Ödenecek tutarın yuvarlanması',
      due: 'Kalan ödeme tutarı',
      advancePayment: 'Mevcut avans ödemesi',
      advanceAmount: 'Eşleştirilecek tutar',
      removeAdvance: 'Eşleştirmeyi kaldır',
      linkAdvance: 'Avansı eşleştir',
      reconcileAdvances: 'Avansları mutabık hale getir',
      prepaymentNotice:
        'Gerçekten mevcut tedarikçi ödemelerini seçin. Eşleştirme yeni ödeme oluşturmaz. Mutabık olmayan avanslar onayı engeller; bu incelemede daha sonra eşleştirebilirsiniz.',
      prepaymentPermission:
        'Banka okuma ve kayıt yetkisi olan bir muhasebeci gerçek ödemeleri mutabık hale getirmelidir.',
      prepaymentUnreconciled:
        'Tedarikçinin önceden ödenen tutarı gerçek ödemelerle mutabık değildir. Elektronik fatura incelemesini yeniden açın ve onaydan önce mevcut avansları eşleştirin.',
      prepaymentInvalid:
        'Aynı tedarikçi ve para birimine ait, kullanılabilir bakiyesi yeterli aktif ödemeleri ve geçerli eşleştirme tutarlarını seçin.',
      sourceAmountsChanged:
        'Taslak tutarlar tedarikçinin özgün belgesinden farklı. Onaydan önce özgün tutarları geri yükleyin.',
      supplierCreditReviewRequired:
        'Bu tedarikçi alacak belgesi incelenebilir taslak olarak saklanır. Tedarikçi alacaklarının kapatılması burada desteklenmez; onay ve giden ödeme engellenir. Muhasebeden özgün faturayla mutabakatını isteyin.',
      title: 'Elektronik faturalar',
      description:
        'Doğrulanmış fatura dosyaları ve özgün belgeleri saklanan, incelenmiş gelen faturalar.',
      import: 'XML veya hibrit PDF içe aktar',
      export: 'Doğrula, arşivle ve indir',
      validating: 'Doğrulanıyor…',
      loading: 'Yükleniyor…',
      retry: 'Yeniden dene',
      save: 'Kaydet',
      search: 'Fatura ara',
      outgoing: 'Giden fatura arşivi',
      incoming: 'Gelen fatura incelemesi',
      outgoingEmpty: 'Kesinleştirilen faturayı belge ayrıntılarından doğrulayın.',
      incomingEmpty: 'İncelemeye başlamak için tedarikçi faturası içe aktarın.',
      invoice: 'Fatura',
      format: 'Biçim',
      validation: 'Doğrulama',
      files: 'Dosyalar',
      download: 'İndir',
      xml: 'XML',
      reports: 'Doğrulama raporları',
      original: 'Özgün dosya',
      valid: 'Geçerli',
      supplier: 'Onaylanan tedarikçi',
      supplierMissing:
        'Mevcut tedarikçiler özgün KDV kimliğiyle eşleşmiyor. Tedarikçiyi tanımlayıp incelemeyi yeniden yükleyin.',
      supplierRequired: 'Tedarikçiyi seçip onaylayın.',
      review: 'İncele',
      imported: 'Taslak oluşturuldu',
      status: 'Durum',
      reviewNotice:
        'Alıcıyı, tutarları, tedarikçiyi ve her sipariş/mal kabul eşlemesini inceleyin. İçe aktarım taslak oluşturur; normal eşleştirme ve onay gereklidir.',
      net: 'Net',
      tax: 'KDV',
      gross: 'Brüt',
      allowances: 'Belge indirimleri',
      charges: 'Belge ek ücretleri',
      openDraft: 'Tedarikçi faturasını aç',
      select: 'Açıkça seçin',
      targetDraft: 'Hedef tedarikçi taslağı',
      newDraft: 'Yeni taslak oluştur',
      purchaseOrder: 'Satın alma siparişi',
      receipt: 'Kaydedilmiş mal kabul',
      line: 'Fatura satırı',
      noMapping: 'Eşleme yok',
      createDraft: 'Onayla ve tedarikçi taslağı oluştur',
      settings: 'Fatura ayarları ve hazır olma durumu',
      ready: 'Hazır',
      notReady: 'Kurulum gerekli',
      setupNotice:
        'Resmî yerel doğrulama paketini yönetime kurdurun. Eksik veya geçersiz doğrulayıcılar dışa ve içe aktarımı engeller.',
      defaultFormat: 'Şirket varsayılan biçimi',
      customerFormat: 'Müşteri fatura biçimi',
      none: 'Normal PDF',
      sellerContact: 'Satıcı yetkili adı',
      endpoint: 'Elektronik adres',
      endpointType: 'Adres şeması',
      email: 'E-posta',
      buyerReference: 'Alıcı referansı / yönlendirme kodu',
      orderReference: 'Alıcı sipariş referansı',
      receiptReference: 'Mal kabul referansı',
      exemptionReason: 'Vergi muafiyeti nedeni',
      retention: 'Saklama süresi (yıl)',
      frozen:
        'Dışa aktarım, kesinleştirmede sabitlenen satıcı, alıcı, tutar ve PDF düzenini kullanır. Arşivlenen baytlar aynı kalır.',
      finalizeHint:
        'Referansları kesinleştirmeden önce kaydedin. E-posta veya portal tesliminden önce faturayı kesinleştirin, doğrulayın ve arşivleyin.',
      disabledHistory: 'Özellik devre dışı. Mevcut kanıtlar okunabilir durumda kalır.',
      frenchNotice:
        'Dosyalar ve e-posta, Fransa’nın yasal platform iletimini sağlamaz. Onaylı bir platformla sözleşme yapın; ERP şu an aktarım için doğrulanmış dosya sunar, otomatik platform gönderimi yapmaz.',
      costs:
        'Resmî doğrulama dosyaları herkese açıktır. Yerel Java, Ghostscript, veraPDF, depolama ve bakım gerekir. Onaylı platformlar ayrıca abonelik veya işlem ücreti alabilir.',
      limits:
        'Yükleme: tek fatura eki içeren XML veya PDF/A-3 hibrit, en fazla 10 MB. Satırsız minimum/basic profilleri tedarikçi taslağı oluşturamaz. Doğrulama yereldir; dış varlıklara erişmez.',
      error: 'İşlem başarısız. Ayrıntıları inceleyip yeniden deneyin.',
      invalidInvoice: 'Fatura verileri eksik veya tutarsız.',
      fileInvalid: 'En fazla 10 MB boyutunda XML fatura veya hibrit PDF seçin.',
      duplicateInvoice:
        'Bu tedarikçi ve numara için fatura mevcut. İnceleyin; başka taslak oluşturulması engellenir.',
      recipientMismatch: 'Fatura alıcısı bu şirketle eşleşmiyor.',
      duplicateOriginal:
        'Bu fatura XML dosyasının saklanmış bir aslı zaten var. Mevcut incelemeyi açın; başka bir dosya bu kanıtın yerini alamaz.',
      supplierMismatch: 'Seçilen tedarikçinin KDV kimliği özgün faturayla eşleşmiyor.',
      creditorAccountRequired: 'İçe aktarmadan önce tedarikçinin muhasebe hesabını tanımlayın.',
      finalSnapshotRequired: 'Doğrulama ve gönderimden önce faturayı kesinleştirin.',
      validatedArchiveRequired: 'Önce belgeden seçili fatura biçimini doğrulayıp arşivleyin.',
      historicalSnapshotMissing:
        'Bu geçmiş faturanın kanıtlanmış elektronik anlık görüntüsü yok. Otomatik yeniden oluşturma engellenir.',
      validationRejected: 'Bağımsız doğrulama faturayı reddetti. Kural mesajlarını inceleyin.',
      validatorUnavailable:
        'Yerel doğrulayıcılar yok veya sağlama toplamları farklı. Kurulumu yönetime düzelttirin.',
      draftChanged: 'Hedef taslak değişti veya düzenlenemiyor. Yeniden yükleyip inceleyin.',
      previewChanged: 'Önizleme değişti veya içe aktarıldı. İncelemeyi yeniden yükleyin.',
      settingsInvalid: 'Tüm ayarları denetleyip seçilen varsayılan biçimi etkinleştirin.',
      purchaseOrderMismatch: 'Sipariş bu tedarikçiye veya para birimine ait değil.',
      receiptMismatch: 'Aynı tedarikçi ve sipariş satırına ait kaydedilmiş mal kabulü seçin.',
      fileSize: 'En fazla 10 MB boyutunda XML fatura veya hibrit PDF seçin.',
      malformedXml: 'Fatura verileri eksik veya tutarsız.',
      missingValue: 'Fatura verileri eksik veya tutarsız.',
      unsafeXml: 'Fatura verileri eksik veya tutarsız.',
      unsupportedFormat: 'Fatura verileri eksik veya tutarsız.',
      unsupportedType: 'Fatura verileri eksik veya tutarsız.',
      utf8Required: 'Fatura verileri eksik veya tutarsız.',
      xmlComplexity: 'Fatura verileri eksik veya tutarsız.',
      invalidText: 'Fatura verileri eksik veya tutarsız.',
      totalMismatch: 'Fatura verileri eksik veya tutarsız.',
      invalidQuantity: 'Fatura verileri eksik veya tutarsız.',
      invalidAllowance: 'Fatura verileri eksik veya tutarsız.',
      duplicateTaxGroup: 'Fatura verileri eksik veya tutarsız.',
      invalidTaxCategory: 'Fatura verileri eksik veya tutarsız.',
      missingTaxGroup: 'Fatura verileri eksik veya tutarsız.',
      buyerReferenceRequired: 'Alıcı referansı / yönlendirme kodu',
      endpointRequired: 'Elektronik adres',
      routingRequired: 'Alıcı referansı / yönlendirme kodu',
      identityIncomplete: 'Fatura verileri eksik veya tutarsız.',
      formatDisabled: 'Tüm ayarları denetleyip seçilen varsayılan biçimi etkinleştirin.',
      settingsChanged: 'Hedef taslak değişti veya düzenlenemiyor. Yeniden yükleyip inceleyin.',
      lineMappingInvalid: 'Aynı tedarikçi ve sipariş satırına ait kaydedilmiş mal kabulü seçin.',
      platformConnectorUnavailable:
        'Dosyalar ve e-posta, Fransa’nın yasal platform iletimini sağlamaz. Onaylı bir platformla sözleşme yapın; ERP şu an aktarım için doğrulanmış dosya sunar, otomatik platform gönderimi yapmaz.',
      platformContractRequired:
        'Dosyalar ve e-posta, Fransa’nın yasal platform iletimini sağlamaz. Onaylı bir platformla sözleşme yapın; ERP şu an aktarım için doğrulanmış dosya sunar, otomatik platform gönderimi yapmaz.',
      defaultFormatInvalid: 'Tüm ayarları denetleyip seçilen varsayılan biçimi etkinleştirin.',
      fileRequired: 'En fazla 10 MB boyutunda XML fatura veya hibrit PDF seçin.',
      pdfInvalid: 'En fazla 10 MB boyutunda XML fatura veya hibrit PDF seçin.',
      invoiceAttachmentRequired: 'En fazla 10 MB boyutunda XML fatura veya hibrit PDF seçin.',
      pdfConversionFailed:
        'Yerel doğrulayıcılar yok veya sağlama toplamları farklı. Kurulumu yönetime düzelttirin.',
      pdfValidationRejected: 'Bağımsız doğrulama faturayı reddetti. Kural mesajlarını inceleyin.',
      validatorFailed:
        'Yerel doğrulayıcılar yok veya sağlama toplamları farklı. Kurulumu yönetime düzelttirin.',
      snapshotChecksumMismatch:
        'Bağımsız doğrulama faturayı reddetti. Kural mesajlarını inceleyin.',
      archiveChecksumMismatch: 'Bağımsız doğrulama faturayı reddetti. Kural mesajlarını inceleyin.',
      attachmentChecksumMismatch:
        'Bağımsız doğrulama faturayı reddetti. Kural mesajlarını inceleyin.',
      snapshotChanged: 'Hedef taslak değişti veya düzenlenemiyor. Yeniden yükleyip inceleyin.',
      formats: {
        FACTUR_X: 'ZUGFeRD / Factur-X (PDF/A-3)',
        XRECHNUNG_CII: 'XRechnung (CII XML)',
        XRECHNUNG_UBL: 'XRechnung (UBL XML)',
      },
      checks: {
        'Official XSD and Schematron artifacts': 'Resmî XSD ve Schematron dosyaları',
        Java: 'Java',
        Ghostscript: 'Ghostscript',
        veraPDF: 'veraPDF',
        'ICC archival colour profile': 'Arşiv için ICC renk profili',
      },
    },
  },
} satisfies Record<
  SupportedLocale,
  { electronicInvoices: Record<string, string | Record<string, string>> }
>;
